import React, { useEffect } from 'react'

import classNames from 'classnames'
import { Helmet } from 'react-helmet-async'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { AppLayout, PageTracker } from '@acre/design-system'
import { setVerificationCacheInvalidation, useGetCurrentUserQuery } from '@acre/graphql'
import { useFlag } from '@acre/utils'

import MainNav from '@broker-crm-common/Nav/MainNav'
import useShouldOnboard from '@broker-crm-hooks/useShouldOnboard'
import { helmetData, useBrandedTitleTemplate } from '@broker-crm-utils'

const AppRoutes = () => {
  const location = useLocation()
  const { caseId, documentId } = useParams<{ caseId: string; documentId: string }>()

  useEffect(() => {
    // Check if the previous URL contained 'sourcing-frontend'
    const previousUrl = document.referrer
    if (previousUrl.includes('sourcing-frontend')) {
      // If the previous URL contained sourcing-frontend, invalidate apollo verification cache
      // We need to retrieve fresh verification data as the user may have made changes in sourcing
      setVerificationCacheInvalidation()
    }
  }, [location])

  const isDocumentViewer = React.useMemo(
    () => location.pathname.endsWith(`documents/${documentId}/cases/${caseId}`),
    [location.pathname, documentId, caseId],
  )

  const isLoginPage = React.useMemo(() => location.pathname === '/login', [location.pathname])

  const { data } = useGetCurrentUserQuery()

  const user = data?.currentUser

  const shouldOnboard = useShouldOnboard(user)

  const titleTemplate = useBrandedTitleTemplate()
  const isScoreRebrand = useFlag('feature.score_rebrand', false)

  // Branded baseline for the tab title. An inline index.html script writes it
  // before first paint when the flag is known at boot (config or override);
  // this effect covers the flag resolving only after mount (org/user
  // metadata), because Helmet's DOM emission does not run under the Vite dev
  // server. Flag off writes nothing: the static index.html title stands.
  useEffect(() => {
    if (isScoreRebrand) {
      document.title = 'Score. Broker CRM'
    }
  }, [isScoreRebrand])

  if (shouldOnboard && !location.pathname.includes('welcome')) {
    return <Navigate to="welcome" replace />
  }

  const className = classNames('router', {
    'router--navigation-spacer': !isLoginPage,
  })

  let title = window.location.pathname.split('/').pop()?.replace(/-/g, ' ') || 'unknown'
  title = title.substring(0, 1).toUpperCase() + title.substring(1, 999)

  return (
    <PageTracker helmetData={helmetData}>
      <div className={className}>
        <Helmet titleTemplate={titleTemplate} helmetData={helmetData}>
          <title>{title}</title>
        </Helmet>
        <AppLayout>
          {!shouldOnboard && !isDocumentViewer && <MainNav />}
          <Outlet />
        </AppLayout>
      </div>
    </PageTracker>
  )
}

export default AppRoutes
